/* Reading a workbook the way the team actually builds them.
 *
 * Three things every real file here does that a naive reader gets wrong.
 *
 * The header is not row one. The pole tracker's Summary tab has a title on
 * row 1, a subtitle on row 2, a blank on row 3 and the header on row 4. The
 * invoice aging Raw Data tab puts its header on row 5. So the header is found,
 * not assumed.
 *
 * There is often a blank first column. Every sheet in the aging workbook is
 * indented by one. Columns that are empty top to bottom are dropped.
 *
 * A sheet is not always data. Dashboards, notes tabs and chart sheets have no
 * usable header, and importing them as tables produces nonsense. Those are
 * detected and offered as skipped rather than silently included.
 */

const MAX_ROWS = 5000;
const HEADER_SEARCH_DEPTH = 12;

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

const asText = (v) => {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).trim();
};

/* Headers are typed with line breaks in them all the time: the aging trend
   sheet labels a column "Feb 2026" over "as of 3/5/2026". One line here. */
const flatLabel = (v) => asText(v).replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ");

/* A key that survives being a JSON property and stays recognisable in a chart
   legend. Duplicated headers get a suffix rather than silently overwriting.
   Exported because a column typed by hand needs the same treatment as one read
   off a header row, and a second slugger would eventually disagree. */
export function keyFor(label, taken) {
  const base = asText(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "column";
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}_${n++}`;
  taken.add(key);
  return key;
}

/* The header row is the widest row in the first dozen that has at least two
   non-blank cells, is mostly text rather than numbers, and has data under it.
   A title row fails on the first test, a subtitle on the second, and the last
   row of a sheet on the third.
 *
 * Widest, not first. Both VIDE trackers open with a block of label and value
 * pairs, Project Name, Client, Reporting Period, Prepared by. Every one of
 * those rows is two text cells with data underneath, so taking the first
 * plausible row locks onto "Project Name:" and reads the whole sheet as two
 * columns of nothing. The real header of that file is nine columns wide and
 * six rows further down. Where two rows tie, the earlier one wins, which is
 * the old behaviour. */
function findHeaderRow(rows) {
  const limit = Math.min(HEADER_SEARCH_DEPTH, rows.length);
  let best = -1;
  let width = 0;

  for (let i = 0; i < limit; i += 1) {
    const cells = rows[i] ?? [];
    const filled = cells.filter((c) => !isBlank(c));
    if (filled.length < 2 || filled.length <= width) continue;
    const texty = filled.filter((c) => typeof c === "string" && Number.isNaN(Number(c)));
    if (texty.length < filled.length * 0.6) continue;
    const below = rows.slice(i + 1, i + 4).some((r) => (r ?? []).some((c) => !isBlank(c)));
    if (!below) continue;
    best = i;
    width = filled.length;
  }
  return best;
}

/* Which columns carry anything at all, header included. */
function usedColumns(rows, headerRow) {
  const width = Math.max(...rows.map((r) => (r ?? []).length), 0);
  const used = [];
  for (let c = 0; c < width; c += 1) {
    const headerFilled = !isBlank(rows[headerRow]?.[c]);
    const bodyFilled = rows.slice(headerRow + 1).some((r) => !isBlank(r?.[c]));
    if (headerFilled && bodyFilled) used.push(c);
  }
  return used;
}

const toNumber = (v) => {
  if (typeof v === "number") return v;
  if (isBlank(v)) return null;
  const s = String(v).replace(/[$,\s]/g, "").replace(/%$/, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const toDate = (v) => {
  if (v instanceof Date) {
    /* Sliced from the local parts, not toISOString, so a date typed in the
       territory does not shift a day on the way in. */
    const p = (n) => String(n).padStart(2, "0");
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ""));
  if (m) return m[0];
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(asText(v));
  if (us) {
    const y = us[3].length === 2 ? `20${us[3]}` : us[3];
    return `${y}-${String(+us[1]).padStart(2, "0")}-${String(+us[2]).padStart(2, "0")}`;
  }
  return null;
};

/* Type comes from the values, not the header. A column of dates charts on a
   time axis; a column of numbers can be summed; everything else groups. */
function inferType(values) {
  const present = values.filter((v) => !isBlank(v));
  if (!present.length) return "text";
  const dates = present.filter((v) => v instanceof Date || toDate(v) !== null).length;
  if (dates >= present.length * 0.8) return "date";
  const numbers = present.filter((v) => {
    if (typeof v === "number") return true;
    const s = String(v).replace(/[$,%\s]/g, "");
    return s !== "" && Number.isFinite(Number(s));
  }).length;
  if (numbers >= present.length * 0.8) return "number";
  return "text";
}

/* One sheet, profiled. Returns null where the sheet holds no table. */
export function profileSheet(name, rows) {
  const headerRow = findHeaderRow(rows);
  if (headerRow === -1) {
    return { name, usable: false, reason: "No header row that has data under it." };
  }

  const cols = usedColumns(rows, headerRow);
  if (cols.length < 2) {
    return { name, usable: false, reason: "Fewer than two columns with data in them." };
  }

  /* A couple of columns scattered across a sheet fifty wide is a drawing, not
     a table. The team's own Excel maps are exactly that: a grid of coloured
     cells with a two word legend, which finds a header and reads as a table of
     nothing. A real two column sheet, a vendor and its terms, is two columns
     out of two or three, not two out of fifty. */
  const width = Math.max(...rows.map((r) => (r ?? []).length), 0);
  if (cols.length < 3 && width >= 8) {
    return {
      name, usable: false,
      reason: `Only ${cols.length} columns of data across a sheet ${width} wide, ` +
              "which is a drawing rather than a table.",
    };
  }

  const body = rows.slice(headerRow + 1)
    .filter((r) => cols.some((c) => !isBlank(r?.[c])))
    .slice(0, MAX_ROWS);

  if (!body.length) {
    return { name, usable: false, reason: "A header, but no rows under it." };
  }

  const taken = new Set();
  const columns = cols.map((c) => {
    const label = flatLabel(rows[headerRow][c]) || `Column ${c + 1}`;
    return {
      key: keyFor(label, taken),
      label,
      type: inferType(body.map((r) => r?.[c])),
      index: c,
    };
  });

  const data = body.map((r) => {
    const out = {};
    for (const col of columns) {
      const raw = r?.[col.index];
      if (isBlank(raw)) { out[col.key] = null; continue; }
      out[col.key] = col.type === "number" ? toNumber(raw)
        : col.type === "date" ? toDate(raw)
        : asText(raw);
    }
    return out;
  });

  return {
    name, usable: true, headerRow,
    columns: columns.map(({ index, ...rest }) => rest),
    rows: data,
    truncated: rows.length - headerRow - 1 > MAX_ROWS,
  };
}


/* ---------------------------------------------------------------- wide sheets
 *
 * Half the workbooks here are drawn the other way round: one row per measure
 * and one column per month. The invoice aging trend is entirely this shape,
 * with Feb through Jul across the top, and it is the shape a person reaches
 * for when they are building something to look at rather than something to
 * query.
 *
 * Charted as they stand, those sheets produce one bar per measure of height
 * one, which is nonsense. Turned on their side they produce exactly the trend
 * the sheet was drawn to show. So the columns that are periods are detected,
 * and the sheet is offered turned round.
 */

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun",
                     "jul", "aug", "sep", "oct", "nov", "dec"];

/* A column header that names a period. "Feb 2026", "Jul 26", "Feb-26",
   "2026-02" and "Feb 2026 as of 3/5/2026" are all the same month. */
export function periodOf(label) {
  const t = asText(label).toLowerCase();
  if (!t) return null;

  const iso = /^(\d{4})[-/](\d{1,2})\b/.exec(t);
  if (iso && +iso[2] >= 1 && +iso[2] <= 12) {
    return { key: `${iso[1]}-${String(+iso[2]).padStart(2, "0")}`, label: asText(label) };
  }

  const named = new RegExp(`\\b(${MONTH_NAMES.join("|")})[a-z]*[\\s\\-/]*(\\d{2,4})\\b`).exec(t);
  if (named) {
    const m = MONTH_NAMES.indexOf(named[1]) + 1;
    const y = named[2].length === 2 ? `20${named[2]}` : named[2];
    return { key: `${y}-${String(m).padStart(2, "0")}`, label: asText(label) };
  }
  return null;
}

/* Which columns are periods, and only when there are enough of them in a row
   to be a trend rather than a coincidence. */
export function periodColumns(columns = []) {
  const found = columns
    .map((c) => ({ column: c, period: periodOf(c.label) }))
    .filter((x) => x.period);
  return found.length >= 3 ? found : [];
}

/* The same sheet with its periods down the side.
 *
 * Everything that is not a period becomes a label column and is repeated on
 * each row, which is what makes the result chartable by the same engine as
 * every other tracker. */
export function unpivotSheet(sheet) {
  const periods = periodColumns(sheet.columns);
  if (!periods.length) return null;

  const periodKeys = new Set(periods.map((p) => p.column.key));
  const kept = sheet.columns.filter((c) => !periodKeys.has(c.key));

  const columns = [
    ...kept,
    { key: "period", label: "Period", type: "date" },
    { key: "period_label", label: "Period as written", type: "text" },
    { key: "value", label: "Value", type: "number" },
  ];

  const rows = [];
  for (const row of sheet.rows) {
    for (const { column, period } of periods) {
      const raw = row[column.key];
      if (raw === null || raw === undefined || raw === "") continue;
      const base = {};
      for (const c of kept) base[c.key] = row[c.key];
      rows.push({
        ...base,
        /* The first of the month, so it sorts and buckets like any other date. */
        period: `${period.key}-01`,
        period_label: period.label,
        value: toNumber(raw),
      });
    }
  }

  return {
    ...sheet,
    columns,
    rows,
    unpivoted: true,
    periodCount: periods.length,
    originalRowCount: sheet.rows.length,
  };
}

/* ------------------------------------------------------------------- csv
 *
 * Half the exports a client sends are CSV, and the network live export is one
 * of them. It goes through exactly the same profiling as a workbook sheet:
 * find the header, drop the blank columns, take each column's type from its
 * values. The only new problem is the format itself, which is a great deal
 * less simple than it looks.
 */

/* One line of CSV, respecting quotes.
 *
 * A quoted field can contain commas, newlines and quotes of its own, and the
 * escape for a quote is two quotes. Splitting on commas would tear a Comments
 * column in half at the first comma somebody typed. */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;

  /* A BOM at the front would otherwise become part of the first header. */
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];

    if (quoted) {
      if (c !== '"') { field += c; continue; }
      if (src[i + 1] === '"') { field += '"'; i += 1; continue; }
      quoted = false;
      continue;
    }

    if (c === '"') { quoted = true; continue; }
    if (c === ",") { row.push(field); field = ""; continue; }
    if (c === "\r") continue;
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; continue; }
    field += c;
  }

  if (field !== "" || row.length) { row.push(field); rows.push(row); }

  /* A trailing newline leaves one empty row, and a file can end with several. */
  while (rows.length && rows[rows.length - 1].every((c) => c === "")) rows.pop();
  return rows;
}

/* A CSV read as a one sheet workbook, so everything downstream is identical. */
export async function readCsv(file) {
  const text = await file.text();
  const rows = parseCsv(text);
  if (!rows.length) throw new Error(`${file.name} is empty.`);

  /* The sheet takes the file's own name, minus the extension, because that is
     what a person would call it. */
  const name = String(file.name || "data").replace(/\.[^.]+$/, "");
  const profiled = profileSheet(name, rows);
  if (!profiled.usable) {
    throw new Error(
      `${file.name} does not look like a table: ${profiled.reason.toLowerCase()}`,
    );
  }
  return {
    fileName: file.name,
    sheets: [{ ...profiled, wide: unpivotSheet(profiled) }],
  };
}

export const looksLikeCsv = (name = "") => /\.(csv|tsv|txt)$/i.test(name);

/* Every sheet in a workbook, profiled, in the file's own order.
 *
 * The reader is imported at call time rather than at the top of the file. The
 * package ships separate browser and node builds with no shared entry point,
 * and everything above this line is pure, so keeping the import here is what
 * lets the profiling logic be tested against the real workbooks in Node while
 * the app uses the browser build. */
export async function readWorkbook(file, readAllSheets) {
  const read = readAllSheets ?? (async (f) => {
    const mod = await import("read-excel-file/browser");
    return (mod.default ?? mod)(f, { getSheets: true });
  });

  let raw;
  try {
    raw = await read(file);
  } catch (e) {
    throw new Error(
      `${file.name} could not be opened as a workbook. If it is an older .xls, ` +
      `open it in Excel and save it as .xlsx. (${e.message})`,
    );
  }

  const sheets = raw.map((s) => {
    const p = profileSheet(s.sheet, s.data);
    return p.usable ? { ...p, wide: unpivotSheet(p) } : p;
  });
  if (!sheets.some((s) => s.usable)) {
    throw new Error(
      `No sheet in ${file.name} looks like a table. Each one needs a header row ` +
      "with data under it.",
    );
  }
  return { fileName: file.name, sheets };
}

export const looksLikeWorkbook = (name = "") =>
  /\.(xlsx|xlsm|xltx)$/i.test(name);
